import React, { useState } from "react";

import {
  IDatasetInputOutputPair,
  IFieldInputProps,
  InputChangeEvent,
} from "api/model/airflow-task/task-template/TaskTemplateInfo";
import CopyTextWithButton from "components/basic/copy-text-with-button/CopyTextWithButton";
import DatasetTitle from "components/basic/display-titles/DatasetTitle";
import { DatasetDropdown } from "components/basic/dropdowns/dataset/DatasetDropdown";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { Button } from "primereact/button";
import { Column, ColumnEditorOptions } from "primereact/column";
import { confirmPopup } from "primereact/confirmpopup";
import {
  DataTable,
  DataTableRowEditCompleteEvent,
  DataTableRowEditEvent,
  DataTableRowReorderEvent,
} from "primereact/datatable";
import { T } from "services/lang/LanguageService";
import { v4 } from "uuid";
import InfoLabel from "../InfoLabel";

interface ITaskConfigDatasetPairTableProps extends IFieldInputProps {
  value?: IDatasetInputOutputPair[] | null;
  onChange?: (e: InputChangeEvent<IDatasetInputOutputPair[]>) => void;
}

interface IDatasetPairRowModel {
  id: string;
  value: IDatasetInputOutputPair;
}

export default function TaskConfigDatasetPairTable(props: ITaskConfigDatasetPairTableProps) {
  const [dataArr, setDataArr] = useState<IDatasetPairRowModel[]>(mapToList(props.value!!));
  const [editingRows, setEditingRows] = useState<Record<string, boolean>>({});

  const propagateChangesStateUp = (arr: IDatasetPairRowModel[]) => {
    const tmp = {
      property: props.property,
      value: arr.map(e => e.value!!),
    } as InputChangeEvent<IDatasetInputOutputPair[]>;
    props.onChange!!(tmp);
  };

  const confirmWrapper = (event: any, confirmHandler: any) => {
    confirmPopup({
      target: event.currentTarget,
      message: T("confirm-popup.are-you-really-want-to-delete-this-dataset-pair-mapping"),
      icon: "pi pi-exclamation-triangle",
      accept: confirmHandler,
    });
  };

  const showWarn = (warn: string) => {
    easasAlert({ summary: T("alert.invalid"), severity: "warn", detail: warn, life: 3000 });
  };

  const handleEditConfirm = (e: DataTableRowEditCompleteEvent) => {
    const { newData } = e;

    setDataArr(prev =>
      prev.map((item, index) => (index === e.index ? { ...item, value: newData.value } : item))
    );
    propagateChangesStateUp(
      dataArr.map((item, index) => (index === e.index ? { ...item, value: newData.value } : item))
    );
  };

  const handleRowEditCancel = (e: DataTableRowEditEvent) => {
    const data = e.data as IDatasetPairRowModel;
    if (data.value.input == null || data.value.output == null) {
      setDataArr(prev => prev.filter(item => item.id !== data.id));
      setEditingRows(prev => ({ ...prev, [data.id]: false }));
    }
  };

  const handRowReorder = (e: DataTableRowReorderEvent<IDatasetPairRowModel[]>) => {
    setDataArr(e.value);
    propagateChangesStateUp(e.value);
  };

  const handleAdd = () => {
    const newRow = { value: { input: null, output: null }, editMode: true, id: v4() };
    setDataArr(prev => [...prev, newRow]);
    setEditingRows(prev => ({ ...prev, [newRow.id]: true }));
  };

  const handleDelete = (row: IDatasetPairRowModel) => {
    const filtered = [...dataArr.filter(item => item.id !== row.id)];
    setDataArr(prev => prev.filter(item => item.id !== row.id));
    propagateChangesStateUp(filtered);
  };

  const headerTemplate = (
    <div className="flex justify-content-between">
      <span />
      <Button
        icon="pi pi-plus"
        className="component-editor-button icon-green"
        label={T("word.add")}
        onClick={() => handleAdd()}
        disabled={props.fieldOptions?.lockable && !props.allowEditLocked}
        tooltip={T("message.need-to-unlock-to-edit")}
        tooltipOptions={{ showOnDisabled: true }}
      />
    </div>
  );

  const inputDatasetBodyTemplate = (row: IDatasetPairRowModel) => datasetBodyTemplate(row, "input");
  const inputDatasetEditorTemplate = (opt: ColumnEditorOptions) => datasetEditorTemplate(opt, "input");
  const outputDatasetBodyTemplate = (row: IDatasetPairRowModel) => datasetBodyTemplate(row, "output");
  const outputDatasetEditorTemplate = (opt: ColumnEditorOptions) => datasetEditorTemplate(opt, "output");

  const datasetBodyTemplate = (row: IDatasetPairRowModel, type: keyof IDatasetInputOutputPair) => {
    return <DatasetTitle identifier={row.value[type]!!} />;
  };

  const datasetEditorTemplate = (opt: ColumnEditorOptions, type: keyof IDatasetInputOutputPair) => {
    return (
      <DatasetDropdown
        value={opt.rowData.value[type]!!}
        onChange={dataset => opt.editorCallback?.(dataset)}
      />
    );
  };

  const renderDeleteButton = (row: IDatasetPairRowModel) => {
    return (
      <Button
        icon="pi pi-trash"
        className="const-del-btn  p-button-rounded p-button-text p-button-danger"
        onClick={e => confirmWrapper(e, () => handleDelete(row))}
      />
    );
  };

  const onRowEditChange = (e: DataTableRowEditEvent) => {
    const newData = dataArr.at(e.index);
    if (newData?.value?.input == null || newData.value?.output == null) {
      const exist = e.data[newData!.id];
      if (!exist) {
        showWarn(T("alert.datasets-must-be-selected"));
        return;
      }
    }
    setEditingRows(e.data);
  };

  const isLocked = props.fieldOptions?.lockable && !props.allowEditLocked;

  return (
    <div className={`field col-${props.width || 12} md:col-${props.widthMd || 12}`}>
      <InfoLabel htmlFor={`${props.property}`} info={props.info}>
        {props.label}
      </InfoLabel>
      <div className="inline-block">
        <CopyTextWithButton text="" copyableText={props.property} />
      </div>
      <DataTable
        value={dataArr}
        editMode="row"
        dataKey="id"
        onRowEditComplete={handleEditConfirm}
        onRowEditChange={onRowEditChange}
        onRowEditCancel={handleRowEditCancel}
        onRowReorder={handRowReorder}
        editingRows={editingRows}
        header={headerTemplate}
        width="100%"
        size="small"
        reorderableRows
      >
        <Column rowReorder style={{ width: "3em" }} />
        <Column
          field="value.input"
          header={T("word.input")}
          body={inputDatasetBodyTemplate}
          editor={inputDatasetEditorTemplate}
        />
        <Column
          field="value.output"
          header={T("word.output")}
          body={outputDatasetBodyTemplate}
          editor={outputDatasetEditorTemplate}
        />
        <Column rowEditor hidden={isLocked} align="right" className="p-0 w-5rem" />
        <Column body={renderDeleteButton} className="p-0 w-3rem" hidden={isLocked} />
        <Column
          hidden={!isLocked}
          body={
            <Button
              icon="pi pi-lock"
              className="p-button-rounded p-button-text p-button-warning"
              disabled
              tooltip={T("message.need-to-unlock-to-edit")}
              tooltipOptions={{ showOnDisabled: true }}
            />
          }
        />
      </DataTable>
    </div>
  );
}

function mapToList(map: IDatasetInputOutputPair[] | null): IDatasetPairRowModel[] {
  if (map == null) return [];
  return map.map(item => {
    return { value: item, id: v4() };
  });
}
